// NOTE: Deliberately importing from @apollo/client
import { useQuery } from '@apollo/client'
import { useResourceContext } from '@/components/layout/use-resource-context'
import { useIdCompany } from '@/lib/hooks'
import { APP_DISPLAY_NAME_QUERY } from '@/routes/app/queries'
import { DATABASE_DISPLAY_NAME_QUERY } from '@/routes/database/queries'
import { LOAD_BALANCER_DISPLAY_NAME_QUERY } from '@/routes/load-balancer/queries'
import { OBJECT_STORAGE_DISPLAY_NAME_QUERY } from '@/routes/object-storage/queries'
import { PROJECT_DISPLAY_NAME_QUERY } from '@/routes/project/queries'
import { STATIC_SITE_DISPLAY_NAME_QUERY } from '@/routes/static-site/queries'
import { WEBHOOK_QUERY } from '@/routes/webhook/queries'
import type {
  AppDisplayNameQuery,
  AppDisplayNameQueryVariables,
} from '@/routes/app/queries.types.gen'
import type {
  DatabaseDisplayNameQuery,
  DatabaseDisplayNameQueryVariables,
} from '@/routes/database/queries.types.gen'
import type {
  LoadBalancerDisplayNameQuery,
  LoadBalancerDisplayNameQueryVariables,
} from '@/routes/load-balancer/queries.types.gen'
import type {
  ObjectStorageDisplayNameQuery,
  ObjectStorageDisplayNameQueryVariables,
} from '@/routes/object-storage/queries.types.gen'
import type {
  ProjectDisplayNameQuery,
  ProjectDisplayNameQueryVariables,
} from '@/routes/project/queries.types.gen'
import type {
  StaticSiteDisplayNameQuery,
  StaticSiteDisplayNameQueryVariables,
} from '@/routes/static-site/queries.types.gen'
import type {
  WebhookQuery,
  WebhookQueryVariables,
} from '@/routes/webhook/queries.types.gen'

const TitleHeader = ({ type, name }: { type: string; name: string }) => (
  <div className="flex flex-col justify-center px-3 border-b border-sidebar-border h-[3.25rem]">
    <span className="font-display text-[10px] uppercase tracking-wider text-muted-foreground leading-tight">
      {type}
    </span>
    <h2 className="font-display text-sm truncate leading-tight">{name}</h2>
  </div>
)

const AppTitle = ({ slug }: { slug: string }) => {
  const { data } = useQuery<AppDisplayNameQuery, AppDisplayNameQueryVariables>(
    APP_DISPLAY_NAME_QUERY,
    { variables: { appName: slug }, fetchPolicy: 'cache-first' }
  )
  return (
    <TitleHeader type="Application" name={data?.app?.displayName ?? slug} />
  )
}

const DatabaseTitle = ({ slug }: { slug: string }) => {
  const { data } = useQuery<
    DatabaseDisplayNameQuery,
    DatabaseDisplayNameQueryVariables
  >(DATABASE_DISPLAY_NAME_QUERY, {
    variables: { databaseName: slug },
    fetchPolicy: 'cache-first',
  })
  return (
    <TitleHeader type="Database" name={data?.database?.displayName ?? slug} />
  )
}

const StaticSiteTitle = ({ slug }: { slug: string }) => {
  const { data } = useQuery<
    StaticSiteDisplayNameQuery,
    StaticSiteDisplayNameQueryVariables
  >(STATIC_SITE_DISPLAY_NAME_QUERY, {
    variables: { staticSiteName: slug },
    fetchPolicy: 'cache-first',
  })
  return (
    <TitleHeader
      type="Static site"
      name={data?.staticSite?.displayName ?? slug}
    />
  )
}

const LoadBalancerTitle = ({ slug }: { slug: string }) => {
  const { idCompany } = useIdCompany()
  const { data } = useQuery<
    LoadBalancerDisplayNameQuery,
    LoadBalancerDisplayNameQueryVariables
  >(LOAD_BALANCER_DISPLAY_NAME_QUERY, {
    variables: { idCompany: idCompany ?? '', loadBalancerName: slug },
    fetchPolicy: 'cache-first',
  })
  return (
    <TitleHeader
      type="Load balancer"
      name={data?.company?.edgeLoadBalancer?.displayName ?? slug}
    />
  )
}

const ProjectTitle = ({ slug }: { slug: string }) => {
  const { idCompany } = useIdCompany()
  const { data } = useQuery<
    ProjectDisplayNameQuery,
    ProjectDisplayNameQueryVariables
  >(PROJECT_DISPLAY_NAME_QUERY, {
    variables: { idCompany: idCompany ?? '', projectName: slug },
    fetchPolicy: 'cache-first',
  })
  return (
    <TitleHeader
      type="Project"
      name={data?.company?.projectByName?.displayName ?? slug}
    />
  )
}

const WebhookTitle = ({ slug }: { slug: string }) => {
  const { idCompany } = useIdCompany()
  const { data } = useQuery<WebhookQuery, WebhookQueryVariables>(
    WEBHOOK_QUERY,
    {
      variables: { idCompany: idCompany ?? '', idWebhook: slug },
      fetchPolicy: 'cache-first',
    }
  )
  return (
    <TitleHeader
      type="Webhook"
      name={data?.company?.webhook?.endpoint ?? 'Webhook'}
    />
  )
}

const ObjectStorageTitle = ({ slug }: { slug: string }) => {
  const { idCompany } = useIdCompany()
  const { data } = useQuery<
    ObjectStorageDisplayNameQuery,
    ObjectStorageDisplayNameQueryVariables
  >(OBJECT_STORAGE_DISPLAY_NAME_QUERY, {
    variables: { idCompany: idCompany ?? '', idObjectStorage: slug },
    fetchPolicy: 'cache-first',
  })
  return (
    <TitleHeader
      type="Object storage"
      name={data?.company?.objectStorage?.displayName ?? 'Object storage'}
    />
  )
}

export const ContextPanel = () => {
  const resource = useResourceContext()

  if (!resource) {
    return null
  }

  return (
    <aside className="hidden md:flex w-60 bg-sidebar border-r border-sidebar-border flex-col h-screen sticky top-0">
      {resource.type === 'Application' ? (
        <AppTitle slug={resource.name} />
      ) : resource.type === 'Database' ? (
        <DatabaseTitle slug={resource.name} />
      ) : resource.type === 'Static site' ? (
        <StaticSiteTitle slug={resource.name} />
      ) : resource.type === 'Load balancer' ? (
        <LoadBalancerTitle slug={resource.name} />
      ) : resource.type === 'Project' ? (
        <ProjectTitle slug={resource.name} />
      ) : resource.type === 'Object storage' ? (
        <ObjectStorageTitle slug={resource.name} />
      ) : resource.type === 'Webhook' ? (
        <WebhookTitle slug={resource.name} />
      ) : (
        <TitleHeader type={resource.type} name={resource.name} />
      )}
      <div className="flex-1 overflow-y-auto pt-1 pb-2">{resource.node}</div>
    </aside>
  )
}
